import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Button } from '@packages/utils';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import PageWrapper from 'components/Base/PageWrapper';
import { EIconNames } from 'components/Base/Icon';
import {
  useGetOrganizationsCitiesQuery,
  useGetOrganizationsListQuery,
  useExportOrganizationsMutation,
} from 'redux/apiSlice/organizations.slice';
import useTable from 'components/Base/Table/hooks/useTable';
import CardContent from 'components/Base/Layout/Content/CardContent';
import {
  EOrganizationFilterParams,
  EOrganizationSortParams,
  TOrganizationDataUI,
} from 'data/types/organizations.types';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { Table } from 'components/Base/Table';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import { TRtkErrorType } from 'data/types/generic.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { ETagType } from 'data/types/tags.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { RoutesPaths } from 'routes/Routes.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { useShowToastNotification } from 'components/Base/Notification';
import useGetColumnsData, {
  organizationListKey,
} from './OrganizationList.data';
import { EOrganizationDetailsTabsInUrl } from '../OrganizationDetails/OrganizationDetails.types';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EOrganizationSortParams.CREATION_DATE]: 'createdDate',
  [EOrganizationFilterParams.NAME_CONTAINS]: 'name',
  [EOrganizationFilterParams.CITIES]: 'address',
  [EOrganizationFilterParams.TAGS]: 'tags',
};

const OrganizationList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation({
    keyPrefix: organizationListKey,
  });
  const { defaultColumns } = useGetColumnsData();
  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    columns,
    setColumns,
    hasFilters,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TOrganizationDataUI>({
    storagePath: ETableStoragePaths.ORGANIZATION,
    defaultColumns,
  });

  const onRowClick = (orgId: string) => {
    navigate(
      `/app/${RoutesPaths.ORGANIZATION}/${orgId}?tab=${EOrganizationDetailsTabsInUrl.DETAILS}`,
      {
        state: {
          backUrl: window.location.pathname,
          backButtonSuffix: t('TITLE'),
        } as TBackButtonState,
      },
    );
  };

  const [openCreateOrganizationModal, setOpenCreateOrganizationModal] =
    useState<TCreateOrganizationModalOpen>({
      visible: false,
      initialOrgName: '',
    });

  const { data, isFetching, isLoading, error } = useGetOrganizationsListQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
  });

  const { data: tagsFilters } = useGetTagsListQuery(ETagType.ORGANIZATION);

  const { data: cities } = useGetOrganizationsCitiesQuery();

  const citiesFilters = useMemo(
    () =>
      cities?.map(city => ({
        text: city,
        value: city,
      })),
    [cities],
  );

  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;
  const canAddOrganization = useCheckPermission('ORGANIZATION_ADD');

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EOrganizationFilterParams.CITIES) {
          item.filters = citiesFilters;
        }
        if (item.key === EOrganizationFilterParams.TAGS) {
          item.filters = tagsFilters;
        }
        return item;
      }),
    );
  }, [citiesFilters, tagsFilters]);

  const [exportOrganizations, { isLoading: isExporting }] =
    useExportOrganizationsMutation();

  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.ORGANIZATION
    ] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.cities) {
        filters.cities = selectedFilters.cities;
      }
      if (selectedFilters?.tagIds) {
        filters.tags = selectedFilters.tagIds;
      }
      if (selectedFilters?.nameContains?.[0]) {
        filters.name = selectedFilters.nameContains[0] as string;
      }
      return Object.keys(filters).length > 0 ? filters : undefined;
    },
    [selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    let field = sortColumn.sortField;
    if (field === EOrganizationFilterParams.NAME_CONTAINS) {
      field = EOrganizationSortParams.ORGANIZATION_NAME;
    }
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportOrganizations({
        columns: exportColumns,
        ...(buildExportFilters() && { filters: buildExportFilters() }),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportOrganizations,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportOrganizations({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportOrganizations,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const headerActions = useMemo(
    () => [
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />,
    ],
    [handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <>
      <PageWrapper
        title={t('TITLE')}
        actions={
          <>
            {canAddOrganization && (
              <Button
                onClick={() =>
                  setOpenCreateOrganizationModal({
                    visible: true,
                  })
                }
                color="primary"
                type="primary"
              >
                {t('actions.add-organization.ADD_ORGANIZATION_BTN')}
              </Button>
            )}
          </>
        }
        isLoading={isLoading}
        error={error as TRtkErrorType}
        emptyWrapper={{
          isEmpty,
          alt: 'People Group',
          icon: EIconNames.PEOPLE_GROUP_FILLED,
          btnText: t('empty-view.BTN_TEXT'),
          emptyText: t('empty-view.EMPTY_TEXT'),
          onClick: () =>
            setOpenCreateOrganizationModal({
              visible: true,
            }),
        }}
      >
        <CardContent>
          <Table<TOrganizationDataUI>
            rowKey={(record: TOrganizationDataUI) => record.organizationId}
            currentPage={page}
            columns={columns}
            dataSource={data?.content}
            rowClassName="pointer"
            loading={isFetching}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalDataCount={data?.totalElements}
            onChange={handleTableChange}
            hasFilters={hasFilters}
            onFiltersReset={onFiltersReset}
            onRow={(record: TOrganizationDataUI) => ({
              onClick: () => onRowClick(record.organizationId),
            })}
            storagePath={ETableStoragePaths.ORGANIZATION}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            headerActions={headerActions}
          />
        </CardContent>
      </PageWrapper>
      <CreateOrganizationModal
        open={openCreateOrganizationModal}
        setOpen={setOpenCreateOrganizationModal}
      />
    </>
  );
};

export default OrganizationList;
